import React, { ReactNode } from "react";
import { Box, Grid, MenuItem, Select, Typography } from "@mui/material";
import { IntegrationToggleOption } from "~/pages/integrations/components/toggle-option/IntegrationToggleOption";
import { motion } from "motion/react";
import { Controller } from "react-hook-form";
import { SubField } from "~/components/Form/components";
import { Control } from "react-hook-form/dist/types/form";
import { EmailFormInput } from "~/pages/integrations/hosted-integrations/forms/email/EmailIntegrationForm";
import { EmailFormattedRecipient } from "./EmailFormattedRecipient";

interface EmailAutoConfigurationSectionProps {
  control: Control<EmailFormInput>;
  disabled: boolean;
  isRecipientsEmpty: boolean;
  recipients: { email: string; name: string }[] | null | undefined;
  showDefaultRecipientDropdown: boolean;
  showAutoCreateSection: boolean;
}

export const EmailAutoConfigurationSection = ({
  control,
  disabled,
  recipients,
  isRecipientsEmpty,
  showDefaultRecipientDropdown,
  showAutoCreateSection,
}: EmailAutoConfigurationSectionProps) => {
  return (
    <>
      {showAutoCreateSection && (
        <Grid mt={3} size={12}>
          <IntegrationToggleOption
            title="Send drift email to this recipient"
            description="If 'Automatically create tickets on drift' is enabled in space settings, send email to the selected recipient for each new drift case."
            formOptions={{
              name: "autoCreateTickets",
              control,
            }}
          />
          {showDefaultRecipientDropdown && (
            <Box
              component={motion.div}
              initial={false}
              transition={{
                duration: 0.35,
                staggerChildren: 0.25,
              }}
              sx={{ overflow: "hidden" }}
            >
              <Controller
                name="defaultRecipient"
                {...{ control }}
                render={({ field }) => (
                  <SubField
                    configuration="Select a drift destination"
                    caption="Select where to send email for each new drift case."
                    disabled={disabled}
                    field={
                      <Grid
                        sx={{ alignSelf: "flex-end" }}
                        size={{
                          xs: 12,
                          md: 6,
                        }}
                      >
                        <Select
                          {...field}
                          fullWidth
                          disabled={disabled}
                          sx={{
                            "&.MuiInputBase-root": {
                              bgcolor: "code.background",
                            },
                          }}
                          displayEmpty
                          renderValue={(selected: ReactNode) => {
                            const selectedRecipient = recipients?.find(
                              (recipient) => recipient.name === selected,
                            );

                            if (!selectedRecipient) {
                              return <>Select a recipient</>;
                            }

                            const selectedRecipientTitle = (
                              <EmailFormattedRecipient
                                name={selectedRecipient.name}
                                email={selectedRecipient.email}
                              />
                            );

                            if (isRecipientsEmpty) {
                              return (
                                <>
                                  No recipients available – create one in email
                                  integration first
                                </>
                              );
                            }

                            if (!selected || !selectedRecipientTitle) {
                              return <>Select a recipient</>;
                            }

                            return <>{selectedRecipientTitle}</>;
                          }}
                        >
                          {(recipients || []).map((recipient) => (
                            <MenuItem
                              key={recipient.name}
                              value={recipient.name}
                            >
                              {recipient.name} ({recipient.email})
                            </MenuItem>
                          ))}
                        </Select>
                      </Grid>
                    }
                  />
                )}
              />
            </Box>
          )}
        </Grid>
      )}
      {!showAutoCreateSection && <NoOptionsAvailable />}
    </>
  );
};

function NoOptionsAvailable() {
  return (
    <Grid
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
      size={12}
    >
      <Typography sx={{ py: 2, color: "text.secondary" }}>
        There are currently no configurable options for this integration.
      </Typography>
    </Grid>
  );
}
